/**
 * EcoF Product Add-Ons
 * Handles checkbox selection and multi-product cart addition
 */

class ProductAddons {
  constructor() {
    this.form = document.querySelector('product-form form[data-type="add-to-cart-form"]');
    this.addonsList = document.querySelector('[id^="addon-list-"]');
    this.submitButton = document.querySelector('[name="add"]');
    
    if (!this.form || !this.addonsList) return;
    
    this.init();
  }

  init() {
    // Handle checkbox changes
    this.addonsList.addEventListener('change', (e) => {
      if (e.target.classList.contains('ecof-addon-checkbox')) {
        this.handleCheckboxChange(e.target);
      }
    });

    // Intercept form submission
    this.form.addEventListener('submit', (e) => {
      this.handleFormSubmit(e);
    });
  }

  handleCheckboxChange(checkbox) {
    const addonItem = checkbox.closest('.ecof-addon-item');
    
    if (checkbox.checked) {
      addonItem.classList.add('is-checked');
    } else {
      addonItem.classList.remove('is-checked');
    }
  }

  handleFormSubmit(e) {
    const selectedAddons = this.getSelectedAddons();
    
    // Always prevent default to ensure consistent behavior
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    
    if (selectedAddons.length === 0) {
      // No add-ons selected, add only main product
      this.addMainProduct();
    } else {
      // Add main product + all selected add-ons
      this.addMultipleProducts(selectedAddons);
    }
  }

  getSelectedAddons() {
    const checkboxes = this.addonsList.querySelectorAll('.ecof-addon-checkbox:checked');
    return Array.from(checkboxes).map(checkbox => {
      const variantId = parseInt(checkbox.dataset.variantId, 10);
      console.log('Add-on variant ID:', variantId, 'from dataset:', checkbox.dataset.variantId);
      return {
        id: variantId,
        quantity: 1
      };
    });
  }

  async addMainProduct() {
    // Disable button and show loading
    this.setButtonState(true);

    try {
      // Get main product variant ID and quantity
      const mainVariantId = this.form.querySelector('[name="id"]').value;
      const mainQuantity = this.form.querySelector('[name="quantity"]')?.value || 1;

      // Add main product to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: mainVariantId,
            quantity: parseInt(mainQuantity)
          }]
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add product to cart');
      }

      await response.json();

      // Trigger cart drawer open
      this.openCartDrawer();
      this.setButtonState(false);

    } catch (error) {
      console.error('Error adding product to cart:', error);
      this.showErrorMessage();
      this.setButtonState(false);
    }
  }

  async addMultipleProducts(addons) {
    // Disable button and show loading
    this.setButtonState(true);

    try {
      // Get main product variant ID and quantity
      const mainVariantId = parseInt(this.form.querySelector('[name="id"]').value, 10);
      const mainQuantity = this.form.querySelector('[name="quantity"]')?.value || 1;

      // Build items array (main product + add-ons)
      const items = [
        {
          id: mainVariantId,
          quantity: parseInt(mainQuantity, 10)
        },
        ...addons
      ];

      console.log('=== Add to Cart Debug ===');
      console.log('Main variant ID:', mainVariantId);
      console.log('Main quantity:', mainQuantity);
      console.log('Add-ons:', addons);
      console.log('Full items array:', items);
      console.log('JSON payload:', JSON.stringify({ items }));

      // Add all items to cart in one request
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Cart API Error Response:', errorText);
        throw new Error('Failed to add products to cart');
      }

      await response.json();

      // Show success message
      this.showSuccessMessage(items.length);

      // Trigger cart drawer open
      this.openCartDrawer();
      this.setButtonState(false);

    } catch (error) {
      console.error('Error adding products to cart:', error);
      this.showErrorMessage();
      this.setButtonState(false);
    }
  }

  openCartDrawer() {
    // Try multiple methods to open cart drawer
    
    // Method 1: Trigger cart drawer open (Dawn/modern themes)
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return;
    }

    // Method 2: Dispatch custom event for cart drawer
    document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
    
    // Method 3: Click cart icon to open drawer
    const cartIcon = document.querySelector('a[href="/cart"]');
    if (cartIcon) {
      cartIcon.click();
    }

    // Method 4: Trigger theme-specific events
    window.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  setButtonState(loading) {
    if (!this.submitButton) return;

    if (loading) {
      this.submitButton.setAttribute('disabled', 'disabled');
      this.submitButton.classList.add('loading');
      
      const spinner = this.submitButton.querySelector('.loading__spinner');
      if (spinner) {
        spinner.classList.remove('hidden');
      }
    } else {
      this.submitButton.removeAttribute('disabled');
      this.submitButton.classList.remove('loading');
      
      const spinner = this.submitButton.querySelector('.loading__spinner');
      if (spinner) {
        spinner.classList.add('hidden');
      }
    }
  }

  showSuccessMessage(itemCount) {
    // Create temporary success message
    const message = document.createElement('div');
    message.className = 'ecof-addons-success';
    message.textContent = `Added ${itemCount} ${itemCount === 1 ? 'item' : 'items'} to cart!`;
    message.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #98CA3F;
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      font-weight: 600;
      z-index: 10000;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      animation: slideInRight 0.3s ease;
    `;

    document.body.appendChild(message);

    setTimeout(() => {
      message.style.animation = 'slideOutRight 0.3s ease';
      setTimeout(() => message.remove(), 300);
    }, 2000);
  }

  showErrorMessage() {
    const message = document.createElement('div');
    message.className = 'ecof-addons-error';
    message.textContent = 'Error adding products to cart. Please try again.';
    message.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #c62828;
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      font-weight: 600;
      z-index: 10000;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    `;

    document.body.appendChild(message);

    setTimeout(() => {
      message.remove();
    }, 3000);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductAddons();
  });
} else {
  new ProductAddons();
}

// Reinitialize on Shopify section render (theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new ProductAddons();
  });
}
